import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Form,
  Spinner,
  ToggleButton,
  ButtonGroup,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { Link } from "react-router-dom";
import "../components/epicLeaderboard/epicLeaderboard.css";
import { useMyPlayer } from "../context/MyPlayerContext";
import {
  useComponentTracking,
  useTimeTracking,
  analytics
} from "../utils/analytics";

const helpIconStyle = {
  fontSize: "11px",
  lineHeight: 1,
  fontWeight: 700,
  textTransform: "none",
  border: "1px solid currentColor",
  borderRadius: "50%",
  width: "16px",
  height: "16px",
  padding: 0,
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "6px",
  cursor: "help",
  verticalAlign: "middle"
};

const getWinRateClass = (rateStr) => {
  const rate = parseFloat(rateStr.replace("%", ""));
  if (rate === 100) return "text-warning";
  if (rate >= 50) return "text-success";
  return "text-danger";
};

const LeaderboardV2 = () => {
  const { isMe } = useMyPlayer();
  useComponentTracking("Leaderboards");
  useTimeTracking("Leaderboards");

  const [type, setType] = useState("pk");
  const [timeframe, setTimeframe] = useState("this_week");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = { type, timeframe };
      if (month) params.month = month;
      const res = await axios.get(
        `${process.env.REACT_APP_API}public/v2/leaderboards`,
        {
          params
        }
      );
      setData(res.data);
      analytics.trackLeaderboardInteraction("view", type, { timeframe, month });
    } catch (err) {
      console.error("Failed to fetch leaderboard", err);
      setData([]);
      analytics.trackError("leaderboard_fetch_error", err.message, {
        type,
        timeframe,
        month
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(
      (row) => {
        const query = search.toLowerCase();
        return (
          row.player.toLowerCase().includes(query) ||
          (row.clan && row.clan.toLowerCase().includes(query))
        );
      },
      [data]
    );
  });

  useEffect(() => {
    fetchLeaderboard();
  }, [type, timeframe, month]);

  return (
    <Container className="py-4">
      <h2 className="mb-4 text-light">Leaderboard: Players</h2>
      <Form>
        <Row className="align-items-end mb-3 text-light">
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <ButtonGroup className="d-flex" toggle="true">
                {["pk", "hc"].map((val) => (
                  <ToggleButton
                    key={val}
                    type="radio"
                    variant="dark"
                    name="type"
                    value={val}
                    checked={type === val}
                    onClick={() => {
                      setType(val);
                      analytics.trackLeaderboardInteraction(
                        "filter_change",
                        val,
                        { filter_type: "type" }
                      );
                    }}
                    className="segmented-toggle text-uppercase fw-bold"
                  >
                    {val.toUpperCase()}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>Timeframe</Form.Label>
              <Form.Select
                value={month ? "" : timeframe}
                onChange={(e) => {
                  setTimeframe(e.target.value);
                  analytics.trackLeaderboardInteraction(
                    "filter_change",
                    e.target.value,
                    { filter_type: "timeframe" }
                  );
                }}
                disabled={!!month}
              >
                <option value="">---</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label>Month</Form.Label>
              <Form.Control
                type="month"
                value={month}
                min="2023-01"
                max={new Date().toISOString().slice(0, 7)}
                onChange={(e) => {
                  setMonth(e.target.value);
                  analytics.trackLeaderboardInteraction("filter_change", e.target.value, { filter_type: "month" });
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="text-light">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search player or clan"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (e.target.value) {
                    analytics.trackSearch(e.target.value, "leaderboard_player_clan");
                  }
                }}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" variant="light" />
        </div>
      ) : (
        <div className="epic-lb-table-scroll">
        <table className="epic-lb-table">
          <thead>
            <tr>
              <th>
                Rank
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Ranked by total kills in the selected timeframe, highest
                      first. No tiebreaker; win rate does not affect rank.
                    </Tooltip>
                  }
                >
                  <span style={helpIconStyle}>?</span>
                </OverlayTrigger>
              </th>
              <th>Player / Clan</th>
              <th>PKs</th>
              <th>
                Win Rate
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      kills / (kills + deaths) for the selected timeframe.
                      Display only, not used for ranking.
                    </Tooltip>
                  }
                >
                  <span style={helpIconStyle}>?</span>
                </OverlayTrigger>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center">
                  No results
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr key={row.rank}>
                  <td>{row.rank}</td>
                  <td>
                    <div className="d-flex flex-column">
                      <Link
                        to={`/player?player=${encodeURIComponent(row.player)}`}
                        className={`fw-bold text-decoration-none text-light${isMe(row.player) ? " is-me" : ""}`}
                        onClick={() => analytics.trackLeaderboardInteraction("player_click", row.player, { rank: row.rank, type })}
                      >
                        {row.player}
                      </Link>
                      <small className="text-muted">{row.clan || "-"}</small>
                    </div>
                  </td>
                  <td>{row.pks}</td>
                  <td className={getWinRateClass(row.winRate)}>
                    {row.winRate}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      )}
    </Container>
  );
};

export default LeaderboardV2;
